// Product Control Plane — the Local AI models editor (product `gao-local-ai`, component `local_ai_models`).
//
// The same draft → review → governed publish path as Explorer's catalog_grid editor; only the rows differ. A row is one
// model RELEASE: the immutable package the Gao App downloads and verifies (read-only here — a changed package is a new
// release, added from its own record) and what the operator decides about it: the name and one line people read, its
// order, where it is served (environment) and what it means on a device (state, and for DEPRECATED/REVOKED the reason a
// person is told).
//
// The Control Plane never downloads, installs, activates or runs a model. A Download and "Use this model" stay the
// person's acts on their own device.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { AuthorityNotice, GAOSAFE_APPROVAL_REASON, useControlPlaneWritesBlocked } from "../authority";
import { getDraft, resetDraft, saveDraft } from "../controlPlane/api";
import { ApiHttpError } from "../api/client";
import {
  ITEM_ENVIRONMENTS,
  validateDocumentRows,
  withModelItems,
  modelItems,
  type ControlPlaneDocument,
  type ItemEnvironment,
  type Lane,
} from "../controlPlane/contract";
import {
  COMPONENT_TYPE_LOCAL_AI_MODELS,
  LOCAL_AI_STATES,
  PRODUCT_GAO_LOCAL_AI,
  STATE_TEXT,
  allowedStates,
  humanBytes,
  inModelOrder,
  packageNameOf,
  parseReleaseRecord,
  type LocalAIModelItem,
  type LocalAIState,
} from "../controlPlane/localAI";
import { laneHost, useControlPlaneTheme } from "../controlPlane/theme";
import { Btn, Card, CpHeader, Empty, ErrorNote, LaneChip, StatusChip } from "../controlPlane/ui";
import { tdStyle, thStyle } from "../controlPlane/styles";
import { ControlPlaneEditor } from "./ControlPlaneEditor";

function describeError(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

const cellInput: React.CSSProperties = { padding: "4px 6px", fontSize: 12 };

/** `/control-plane/:productId/edit` — gao-local-ai's rows are models, every other product's a catalog grid. */
export function ControlPlaneEditRoute() {
  const { productId = "" } = useParams();
  return productId === PRODUCT_GAO_LOCAL_AI ? <LocalAIModelsEditor /> : <ControlPlaneEditor />;
}

export function LocalAIModelsEditor() {
  const { productId = PRODUCT_GAO_LOCAL_AI } = useParams();
  const navigate = useNavigate();
  const { themeProps, toggle, theme } = useControlPlaneTheme();

  const [doc, setDoc] = useState<ControlPlaneDocument | null>(null);
  const [componentId, setComponentId] = useState<string>("");
  const [items, setItems] = useState<LocalAIModelItem[]>([]);
  const [loadedStates, setLoadedStates] = useState<Map<string, LocalAIState>>(new Map());
  const [lane, setLane] = useState<Lane | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [loadedHash, setLoadedHash] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [recordText, setRecordText] = useState("");
  const [addEnvironment, setAddEnvironment] = useState<ItemEnvironment>("test");
  const [addState, setAddState] = useState<LocalAIState>("STAGED");
  const [addProblems, setAddProblems] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await getDraft(productId);
      setDoc(d.document);
      setLoadedHash(d.documentHash);
      const component = d.document.surfaces
        .flatMap((s) => s.components)
        .find((c) => c.type === COMPONENT_TYPE_LOCAL_AI_MODELS);
      setComponentId(component?.id ?? "");
      const rows = inModelOrder(modelItems(d.document));
      setItems(rows);
      setLoadedStates(new Map(rows.map((r) => [r.id, r.state])));
      setDirty(false);
    } catch (e) {
      setError(describeError(e));
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    import("../controlPlane/api").then(async (m) => {
      try {
        const r = await m.listProducts();
        if (!cancelled) setLane(r.lane);
      } catch {
        /* the banner degrades; the server still decides the real lane */
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const issues = useMemo(
    () => (doc ? validateDocumentRows(withModelItems(doc, componentId, items)) : []),
    [doc, componentId, items],
  );
  const issuesByItem = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const i of issues) m.set(i.itemId, [...(m.get(i.itemId) ?? []), i.message]);
    return m;
  }, [issues]);

  function patch(index: number, next: (it: LocalAIModelItem) => LocalAIModelItem) {
    setItems((prev) => prev.map((it, i) => (i === index ? next(it) : it)));
    setDirty(true);
  }

  function presentation(index: number, next: Partial<LocalAIModelItem["release"]["presentation"]>) {
    patch(index, (it) => ({ ...it, release: { ...it.release, presentation: { ...it.release.presentation, ...next } } }));
  }

  function setState(index: number, state: LocalAIState) {
    patch(index, (it) => ({
      ...it,
      state,
      // A reason belongs to DEPRECATED and REVOKED only; the operator writes it (never a default).
      reason: state === "DEPRECATED" || state === "REVOKED" ? (it.reason ?? "") : null,
    }));
  }

  function remove(index: number) {
    setItems((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
  }

  function addModel() {
    const parsed = parseReleaseRecord(recordText);
    if (!parsed.ok) {
      setAddProblems(parsed.problems.slice(0, 12));
      return;
    }
    const id = packageNameOf(parsed.release);
    if (items.some((it) => it.id === id)) {
      setAddProblems([`${id} is already listed — a changed package is a new version`]);
      return;
    }
    const row: LocalAIModelItem = { id, environment: addEnvironment, state: addState, reason: null, release: parsed.release };
    setItems((prev) => inModelOrder([...prev, row]));
    setDirty(true);
    setAdding(false);
    setRecordText("");
    setAddProblems([]);
  }

  async function save() {
    if (!doc) return;
    setSaving(true);
    setError(null);
    try {
      await saveDraft(productId, withModelItems(doc, componentId, items), loadedHash);
      setDirty(false);
      await load();
    } catch (e) {
      const conflict =
        e instanceof ApiHttpError && e.status === 409
          ? "Someone else saved this draft while you were editing. Reload to see their version — your changes on this " +
            "screen have not been saved."
          : null;
      setError(conflict ?? describeError(e));
    } finally {
      setSaving(false);
    }
  }

  async function discard() {
    setDiscardOpen(false);
    setSaving(true);
    setError(null);
    try {
      await resetDraft(productId);
      await load();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setSaving(false);
    }
  }

  const blocked = issues.length > 0;
  const writesBlocked = useControlPlaneWritesBlocked();

  return (
    <div {...themeProps} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <CpHeader
        breadcrumb={
          <>
            <Link to="/control-plane">Control Plane</Link>
            {" / "}
            <Link to={`/control-plane/${productId}`}>{productId}</Link>
          </>
        }
        title="Local AI models"
        subtitle="local-ai · local_ai_models — what the Gao App offers to download"
        actions={
          <>
            <StatusChip tone="warn">EDITING DRAFT — NOT VISIBLE TO ANYONE</StatusChip>
            <LaneChip lane={lane} host={laneHost(lane)} />
            <button type="button" onClick={toggle}>
              {theme === "light" ? "Dark" : "Light"}
            </button>
          </>
        }
      />

      {writesBlocked && <AuthorityNotice scope="Draft edits cannot be saved." />}
      {error !== null && <ErrorNote>{error}</ErrorNote>}

      <ConfirmDialog
        writesBlocked={writesBlocked}
        open={discardOpen}
        tone="danger"
        title="Discard draft changes"
        description="This replaces the draft with the live revision. Unpublished edits are not recoverable — no revision is written for a draft."
        rows={[
          { key: "Product", value: productId },
          { key: "Reverts to", value: "the current live revision" },
        ]}
        confirmLabel="Discard changes"
        busy={saving}
        onConfirm={() => void discard()}
        onCancel={() => setDiscardOpen(false)}
      />

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <Btn onClick={() => setAdding((a) => !a)}>{adding ? "Close" : "+ Add model"}</Btn>
        <Btn
          onClick={() => void save()}
          disabled={writesBlocked || saving || !dirty || blocked}
          title={writesBlocked ? GAOSAFE_APPROVAL_REASON : blocked ? "Fix the issues below before saving" : undefined}
        >
          {saving ? "Saving…" : dirty ? "Save draft" : "Saved"}
        </Btn>
        <Btn
          onClick={() => setDiscardOpen(true)}
          disabled={writesBlocked || saving || !dirty}
          title={writesBlocked ? GAOSAFE_APPROVAL_REASON : undefined}
        >
          Discard changes
        </Btn>
        <span style={{ flex: 1 }} />
        <Btn
          tone="primary"
          disabled={blocked || dirty}
          title={blocked ? "Fix the issues below first" : dirty ? "Save the draft first" : undefined}
          onClick={() => navigate(`/control-plane/${productId}/review`)}
        >
          Review &amp; publish…
        </Btn>
      </div>

      {adding && (
        <Card title="Add model" note="paste the release record written from the package's lock (model-origin.py release)">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <textarea
              aria-label="release record"
              value={recordText}
              onChange={(e) => setRecordText(e.target.value)}
              placeholder='{"schema": "gao.local-ai.release/v1", "release": { … }}'
              rows={10}
              style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}
            />
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <label>
                Environment{" "}
                <select
                  aria-label="new model environment"
                  value={addEnvironment}
                  onChange={(e) => setAddEnvironment(e.target.value as ItemEnvironment)}
                >
                  {ITEM_ENVIRONMENTS.map((e) => (
                    <option key={e} value={e}>
                      {e}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                State{" "}
                <select aria-label="new model state" value={addState} onChange={(e) => setAddState(e.target.value as LocalAIState)}>
                  {allowedStates(null).map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <span style={{ color: "var(--fg-dim)", fontSize: 12 }}>{STATE_TEXT[addState]}</span>
              <span style={{ flex: 1 }} />
              <Btn tone="primary" onClick={addModel} disabled={recordText.trim() === ""}>
                Add to draft
              </Btn>
            </div>
            {addProblems.length > 0 && (
              <ErrorNote>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {addProblems.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </ErrorNote>
            )}
          </div>
        </Card>
      )}

      {blocked && (
        <Card accent="danger" title={`${issues.length} issue${issues.length === 1 ? "" : "s"} block publish`}>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {issues.slice(0, 40).map((i, n) => (
              <li key={`${i.itemId}-${n}`}>
                <b>{i.itemId || "(new row)"}</b> — <span style={{ color: "var(--danger)" }}>{i.message}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card
        title="Models"
        note={`${items.length} · in the order the Gao App lists them · the package itself is read-only`}
        style={{ padding: 0 }}
      >
        {loading ? (
          <Empty>Loading…</Empty>
        ) : items.length === 0 ? (
          <Empty>No models. Add one to begin.</Empty>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100 }}>
              <thead>
                <tr>
                  <th style={thStyle}>Model</th>
                  <th style={thStyle}>One line</th>
                  <th style={{ ...thStyle, width: 70 }}>Order</th>
                  <th style={{ ...thStyle, width: 110 }}>Environment</th>
                  <th style={{ ...thStyle, width: 130 }}>State</th>
                  <th style={thStyle}>Reason (what a person is told)</th>
                  <th style={{ ...thStyle, width: 150 }}>Package</th>
                  <th style={{ ...thStyle, width: 70 }} />
                </tr>
              </thead>
              <tbody>
                {items.map((it, index) => {
                  const rowIssues = issuesByItem.get(it.id) ?? [];
                  const loaded = loadedStates.get(it.id) ?? null;
                  const states = allowedStates(loaded);
                  const p = it.release.presentation;
                  return (
                    <tr key={it.id} style={rowIssues.length > 0 ? { background: "var(--danger-bg, #2a1416)" } : undefined}>
                      <td style={tdStyle}>
                        <input
                          value={p.displayName}
                          aria-label={`${it.id} name`}
                          onChange={(e) => presentation(index, { displayName: e.target.value })}
                          style={{ ...cellInput, width: 140 }}
                        />
                        <div style={{ color: "var(--fg-dim)", fontSize: 11 }}>
                          {it.release.modelId}@{it.release.version}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <input
                          value={p.shortDescription}
                          aria-label={`${it.id} description`}
                          onChange={(e) => presentation(index, { shortDescription: e.target.value })}
                          style={{ ...cellInput, width: 200 }}
                        />
                      </td>
                      <td style={tdStyle}>
                        <input
                          type="number"
                          value={p.order}
                          aria-label={`${it.id} order`}
                          onChange={(e) => presentation(index, { order: Number.parseInt(e.target.value || "0", 10) })}
                          style={{ ...cellInput, width: 56 }}
                        />
                      </td>
                      <td style={tdStyle}>
                        <select
                          aria-label={`${it.id} environment`}
                          value={it.environment}
                          onChange={(e) => patch(index, (x) => ({ ...x, environment: e.target.value as ItemEnvironment }))}
                        >
                          {ITEM_ENVIRONMENTS.map((e) => (
                            <option key={e} value={e}>
                              {e}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={tdStyle} title={STATE_TEXT[it.state]}>
                        <select
                          aria-label={`${it.id} state`}
                          value={it.state}
                          disabled={states.length === 1}
                          onChange={(e) => setState(index, e.target.value as LocalAIState)}
                        >
                          {LOCAL_AI_STATES.filter((s) => states.includes(s)).map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                        {loaded === "REVOKED" && (
                          <div style={{ color: "var(--fg-dim)", fontSize: 11 }}>final — publish a new release instead</div>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {it.state === "DEPRECATED" || it.state === "REVOKED" ? (
                          <input
                            value={it.reason ?? ""}
                            aria-label={`${it.id} reason`}
                            placeholder="required"
                            onChange={(e) => patch(index, (x) => ({ ...x, reason: e.target.value }))}
                            style={{ ...cellInput, width: 220 }}
                          />
                        ) : (
                          <span style={{ color: "var(--fg-dim)" }}>—</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, fontSize: 11 }}>
                        <div>{humanBytes(it.release.package.downloadBytes)}</div>
                        <div style={{ color: "var(--fg-dim)" }} title={it.release.package.manifest.sha256}>
                          manifest {it.release.package.manifest.sha256.slice(0, 12)}…
                        </div>
                        <div style={{ color: "var(--fg-dim)" }}>
                          {it.release.provenance.license} · {it.release.runtime.abi}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        {(loaded === null || loaded === "STAGED") && (
                          <button type="button" aria-label={`Remove ${it.id}`} onClick={() => remove(index)}>
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <div style={{ color: "var(--fg-dim)", fontSize: 12 }}>
        {STATE_TEXT.DEPRECATED[0]!.toUpperCase()}
        {STATE_TEXT.DEPRECATED.slice(1)} (Deprecate). {STATE_TEXT.REVOKED[0]!.toUpperCase()}
        {STATE_TEXT.REVOKED.slice(1)} (Revoke). Only a test release that was never published can be removed; a published one
        is deprecated or revoked, so the Gao App always knows what it has installed.
      </div>
    </div>
  );
}
